import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
  AssetLossFactors,
  ExternalLossFactors,
  LossEventFrequency,
  LossMagnitude,
  OrganizationalLossFactors,
  RiskRegister,
  RiskRegisterAsset,
  RiskRegisterOrganization,
  RiskRegisterService,
  ThreatEventFrequency,
  ThreatLossFactors,
  Vulnerability,
} from "../../../risk-register-service";

export interface RiskRegisterAssetInfoDialogData {
  risk: RiskRegister;
}

@Component({
  selector: "app-risk-register-asset-info-dialog",
  templateUrl: "./risk-register-asset-info-dialog.component.html",
  styleUrls: ["./risk-register-asset-info-dialog.component.scss"],
})
export class RiskRegisterAssetInfoDialogComponent implements OnInit {
  loading = true;
  errorMessage = "";
  assessment: RiskRegister | null = null;
  asset: RiskRegisterAsset | null = null;
  organizationName = "";
  threatEventFrequency: ThreatEventFrequency | null = null;
  vulnerability: Vulnerability | null = null;
  lossEventFrequency: LossEventFrequency | null = null;
  assetLossFactors: AssetLossFactors | null = null;
  threatLossFactors: ThreatLossFactors | null = null;
  organizationalLossFactors: OrganizationalLossFactors | null = null;
  externalLossFactors: ExternalLossFactors | null = null;
  lossMagnitude: LossMagnitude | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RiskRegisterAssetInfoDialogData,
    private dialogRef: MatDialogRef<RiskRegisterAssetInfoDialogComponent>,
    private riskRegisterService: RiskRegisterService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadAssessmentDetails();
  }

  close(): void {
    this.dialogRef.close();
  }

  displayValue(value: unknown): string {
    if (value == null || `${value}`.trim() === "") {
      return "Not entered";
    }

    return `${value}`;
  }

  private async loadAssessmentDetails(): Promise<void> {
    const assessmentId = String(
      this.data.risk.id ?? this.data.risk.assessmentId ?? "",
    );

    if (!assessmentId) {
      this.errorMessage = "Assessment id is missing.";
      this.loading = false;
      return;
    }

    try {
      this.assessment =
        (await this.riskRegisterService
          .getRiskRegisters(assessmentId)
          .toPromise()) ?? this.data.risk;

      const organizationId =
        this.assessment.organizationId ?? this.data.risk.organizationId;

      const [
        organizations,
        assets,
        threatEventFrequency,
        vulnerability,
        lossEventFrequency,
        assetLossFactors,
        threatLossFactors,
        organizationalLossFactors,
        externalLossFactors,
        lossMagnitude,
      ] = await Promise.all([
        this.riskRegisterService.getOrganizations().toPromise(),
        organizationId
          ? this.riskRegisterService.getAssets(organizationId).toPromise()
          : Promise.resolve([] as RiskRegisterAsset[]),
        this.riskRegisterService.getThreatEventFrequency(assessmentId).toPromise(),
        this.riskRegisterService.getVulnerability(assessmentId).toPromise(),
        this.riskRegisterService.getLossEventFrequency(assessmentId).toPromise(),
        this.riskRegisterService.getAssetLossFactors(assessmentId).toPromise(),
        this.riskRegisterService.getThreatLossFactors(assessmentId).toPromise(),
        this.riskRegisterService
          .getOrganizationalLossFactors(assessmentId)
          .toPromise(),
        this.riskRegisterService.getExternalLossFactors(assessmentId).toPromise(),
        this.riskRegisterService.getLossMagnitude(assessmentId).toPromise(),
      ]);

      const assetId = this.assessment.assetId ?? this.data.risk.assetId;
      this.asset =
        (assets ?? []).find(
          (item: RiskRegisterAsset) => item.assetId === assetId,
        ) ?? null;
      this.organizationName =
        (organizations ?? []).find(
          (organization: RiskRegisterOrganization) =>
            organization.organizationId === organizationId,
        )?.organizationName ?? "Not entered";
      this.threatEventFrequency = threatEventFrequency ?? null;
      this.vulnerability = vulnerability ?? null;
      this.lossEventFrequency = lossEventFrequency ?? null;
      this.assetLossFactors = assetLossFactors ?? null;
      this.threatLossFactors = threatLossFactors ?? null;
      this.organizationalLossFactors = organizationalLossFactors ?? null;
      this.externalLossFactors = externalLossFactors ?? null;
      this.lossMagnitude = lossMagnitude ?? null;
    } catch (error) {
      console.error("Error loading assessment details:", error);
      this.errorMessage = "Failed to load assessment details.";
    } finally {
      this.loading = false;
    }
  }
}
